/**
 * 继电保护计算工具函数库
 * 包含各种保护类型的计算逻辑
 */

// IEC 60255 反时限曲线常数与计算
const IEC_CURVES = {
  NI: { k: 0.14, alpha: 0.02, name: 'Normal Inverse' },
  VI: { k: 13.5, alpha: 1, name: 'Very Inverse' },
  EI: { k: 80, alpha: 2, name: 'Extremely Inverse' },
  LTI: { k: 120, alpha: 1, name: 'Long-time Inverse' }
};
const normalizeCurveType = (t) => {
  const map = {
    'Normal Inverse': 'NI',
    'Very Inverse': 'VI',
    'Extremely Inverse': 'EI',
    'Long-time Inverse': 'LTI',
    'NI': 'NI', 'VI': 'VI', 'EI': 'EI', 'LTI': 'LTI'
  };
  return map[t] || 'NI';
};
const computeInverseTime = (M, TMS, curveType) => {
  const key = normalizeCurveType(curveType);
  const c = IEC_CURVES[key] || IEC_CURVES.NI;
  if (M <= 1) return Infinity;
  const t = TMS * c.k / (Math.pow(M, c.alpha) - 1);
  return parseFloat(t.toFixed(3));
};
const generateCurvePoints = (TMS, curveType, Mmin = 1.1, Mmax = 20, steps = 50) => {
  const points = [];
  const step = (Mmax - Mmin) / steps;
  for (let i = 0; i <= steps; i++) {
    const M = +(Mmin + i * step).toFixed(2);
    const t = computeInverseTime(M, TMS, curveType);
    points.push({ M, t: +t });
  }
  return points;
};

/**
 * 过电流保护计算
 * @param {Object} params - 计算参数
 * @param {number} params.ratedCurrent - 额定电流
 * @param {number} params.ctRatio - CT变比
 * @param {number} params.faultCurrent - 故障电流
 * @param {string} [params.curveType] - 曲线类型 (NI/VI/EI/LTI 或完整名称)
 * @param {number} [params.timeMultiplier] - 时间倍数 TMS
 * @returns {Object} 计算结果
 */
export const calculateOvercurrent = (params) => {
  const { ratedCurrent, ctRatio, faultCurrent, curveType: curveInput, timeMultiplier } = params;
  
  // 故障电流（一次值）
  const primaryCurrent = faultCurrent;
  // 故障电流（二次值）
  const secondaryCurrent = primaryCurrent / ctRatio;
  
  // 启动电流设定：优先使用 maxLoadCurrent（最大负荷），否则使用 ratedCurrent
  const baseLoadCurrent = (typeof params.maxLoadCurrent === 'number' && params.maxLoadCurrent > 0)
    ? params.maxLoadCurrent
    : ratedCurrent;
  const pickupMultiplier = (typeof params.pickupMultiplier === 'number' && params.pickupMultiplier > 0)
    ? params.pickupMultiplier
    : 1.3; // 默认取 1.3 倍
  const pickupCurrent = (baseLoadCurrent * pickupMultiplier) / ctRatio;
  // 可选：瞬时元件（高倍快速动作）仅作为输出参考，不影响既有测试
  const instantaneousMultiplier = (typeof params.instantaneousMultiplier === 'number' && params.instantaneousMultiplier > 0)
    ? params.instantaneousMultiplier
    : undefined;
  const instantaneousPickup = instantaneousMultiplier
    ? parseFloat(((baseLoadCurrent * instantaneousMultiplier) / ctRatio).toFixed(3))
    : undefined;
  
  // 时间设定 (反时限特性)
  const curveType = curveInput || 'Normal Inverse';
  const TMS = typeof timeMultiplier === 'number' && timeMultiplier > 0 ? timeMultiplier : 0.5; // 时间倍数
  const M = secondaryCurrent / pickupCurrent;
  const operateTime = computeInverseTime(M, TMS, curveType);
  const curvePoints = generateCurvePoints(TMS, curveType);
  
  return {
    axesType: 'I-t',
    pickupCurrent: parseFloat(pickupCurrent.toFixed(3)),
    baseLoadCurrent: parseFloat(baseLoadCurrent.toFixed(3)),
    pickupMultiplier: parseFloat(pickupMultiplier.toFixed(2)),
    ...(instantaneousMultiplier ? { instantaneousMultiplier: parseFloat(instantaneousMultiplier.toFixed(2)) } : {}),
    timeMultiplier: TMS,
    curveType,
    secondaryCurrent: parseFloat(secondaryCurrent.toFixed(3)),
    sensitivity: parseFloat((secondaryCurrent / pickupCurrent).toFixed(2)),
    operateTime,
    curvePoints,
    ...(instantaneousPickup ? { instantaneousPickup } : {})
  };
};

/**
 * 距离保护计算
 * @param {Object} params - 计算参数
 * @param {number} params.systemVoltage - 系统电压(kV)
 * @param {number} params.faultCurrent - 故障电流(A)
 * @param {number} params.ctRatio - CT变比
 * @param {number} params.ptRatio - PT变比
 * @returns {Object} 计算结果
 */
export const calculateDistance = (params) => {
  const { systemVoltage, faultCurrent, ctRatio, ptRatio } = params;
  
  // 故障点阻抗（一次值）
  const primaryImpedance = (systemVoltage * 1000) / faultCurrent;
  // 故障点阻抗（二次值）
  const secondaryImpedance = (primaryImpedance * ctRatio) / ptRatio;
  // 区段覆盖百分比/定值覆盖：支持覆盖输入覆盖
  const clampPct = (v, def) => {
    const n = Number(v);
    if (!isFinite(n) || n <= 0) return def;
    // 合理范围：50%~300%
    const dec = n > 1 ? (n / 100) : n;
    return Math.min(Math.max(dec, 0.5), 3.0);
  };
  const z1Pct = clampPct(params.zone1Percent ?? params?.zoneTargets?.z1, 0.85);
  const z2Pct = clampPct(params.zone2Percent ?? params?.zoneTargets?.z2, 1.2);
  const z3Pct = clampPct(params.zone3Percent ?? params?.zoneTargets?.z3, 1.8);

  // 基准阻抗模式：默认使用工况阻抗 V/I；若启用 useLineParams 且线路参数可用，则改用线路阻抗幅值
  let baseImpedance = secondaryImpedance;
  let baseImpedanceMode = 'fault';
  // 线路阻抗角来源优先级：显式给定 -> 参数R/X -> 设备库馈线
  let lineAngleDeg = typeof params.lineAngleDeg === 'number' ? params.lineAngleDeg : undefined;
  let lineMagPrimary; // 线路正序阻抗幅值（一次值）
  if (!(typeof lineAngleDeg === 'number' && isFinite(lineAngleDeg))) {
    const rParam = params.lineR_ohm;
    const xParam = params.lineX_ohm;
    if (rParam != null && xParam != null) {
      const R = Number(rParam) || 0;
      const X = Number(xParam) || 0;
      const ang = Math.atan2(X, R) * 180 / Math.PI;
      if (isFinite(ang)) lineAngleDeg = ang;
      const mag = Math.sqrt(R * R + X * X);
      if (isFinite(mag)) lineMagPrimary = mag;
    }
  }
  if (!(typeof lineAngleDeg === 'number' && isFinite(lineAngleDeg)) && params.selectedFeederId) {
    try {
      // 延迟加载以避免循环依赖
      const { getFeederById } = require('./deviceLibrary');
      const f = getFeederById(params.selectedFeederId);
      const Rpkm = f?.line?.R_per_km_ohm ?? f?.R_per_km_ohm ?? 0;
      const Xpkm = f?.line?.X_per_km_ohm ?? f?.X_per_km_ohm ?? 0;
      const Lkm = f?.line?.length_km ?? f?.length_km ?? 0;
      const R = (Number(Rpkm) || 0) * (Number(Lkm) || 0);
      const X = (Number(Xpkm) || 0) * (Number(Lkm) || 0);
      const ang = Math.atan2(X, R) * 180 / Math.PI;
      if (isFinite(ang)) lineAngleDeg = ang;
      const mag = Math.sqrt(R * R + X * X);
      if (isFinite(mag)) lineMagPrimary = mag;
    } catch (_) {}
  }
  if (!(typeof lineAngleDeg === 'number' && isFinite(lineAngleDeg))) {
    lineAngleDeg = 75; // 回退默认角度
  }
  lineAngleDeg = parseFloat(Number(lineAngleDeg).toFixed(1));

  // 若启用了线路参数模式且线路阻抗幅值可用，使用线路阻抗幅值（转换至二次值）作为区段基准
  const useLineParams = !!params.useLineParams;
  if (useLineParams && isFinite(lineMagPrimary)) {
    baseImpedance = (lineMagPrimary * ctRatio) / ptRatio;
    baseImpedanceMode = 'line';
  }

  // 阻抗定值设定：优先使用显式定值，否则按覆盖百分比×基准阻抗
  const zone1Setting = (typeof params.zone1Setting === 'number' && isFinite(params.zone1Setting))
    ? Number(params.zone1Setting)
    : baseImpedance * z1Pct;
  const zone2Setting = (typeof params.zone2Setting === 'number' && isFinite(params.zone2Setting))
    ? Number(params.zone2Setting)
    : baseImpedance * z2Pct;
  const zone3Setting = (typeof params.zone3Setting === 'number' && isFinite(params.zone3Setting))
    ? Number(params.zone3Setting)
    : baseImpedance * z3Pct;
  const zoneSpanDeg = [20, 25, 30];
  const margins = [10, 5, 0];
  
  return {
    axesType: 'Z',
    zone1Setting: parseFloat(zone1Setting.toFixed(3)),
    zone2Setting: parseFloat(zone2Setting.toFixed(3)),
    zone3Setting: parseFloat(zone3Setting.toFixed(3)),
    secondaryImpedance: parseFloat(secondaryImpedance.toFixed(3)),
    reach: `${Math.round(z1Pct * 100)}%线路长度`,
    lineAngleDeg,
    baseImpedanceMode,
    ...(useLineParams && isFinite(lineMagPrimary) ? { lineImpedanceSecondary: parseFloat(((lineMagPrimary * ctRatio) / ptRatio).toFixed(3)) } : {}),
    zones: [
      { label: 'Zone 1', Z: parseFloat(zone1Setting.toFixed(3)), thetaStartDeg: lineAngleDeg - zoneSpanDeg[0], thetaEndDeg: lineAngleDeg + zoneSpanDeg[0], spanDeg: zoneSpanDeg[0], marginPct: margins[0] },
      { label: 'Zone 2', Z: parseFloat(zone2Setting.toFixed(3)), thetaStartDeg: lineAngleDeg - zoneSpanDeg[1], thetaEndDeg: lineAngleDeg + zoneSpanDeg[1], spanDeg: zoneSpanDeg[1], marginPct: margins[1] },
      { label: 'Zone 3', Z: parseFloat(zone3Setting.toFixed(3)), thetaStartDeg: lineAngleDeg - zoneSpanDeg[2], thetaEndDeg: lineAngleDeg + zoneSpanDeg[2], spanDeg: zoneSpanDeg[2], marginPct: margins[2] }
    ]
  };
};

/**
 * 差动保护计算
 * @param {Object} params - 计算参数
 * @param {number} params.ratedCurrent - 额定电流
 * @param {number} params.ctRatio - CT变比
 * @param {number} params.transformerCapacity - 变压器容量
 * @returns {Object} 计算结果
 */
export const calculateDifferential = (params) => {
  const { ratedCurrent, ctRatio, transformerCapacity } = params;
  
  // 若未提供额定电流，可选用容量与系统电压推导（I≈S/(√3·U)），保持兼容：默认仍优先使用 ratedCurrent
  const sysUkv = typeof params.systemVoltage === 'number' && params.systemVoltage > 0 ? params.systemVoltage : undefined;
  const derivedRated = (!ratedCurrent && transformerCapacity && sysUkv) 
    ? (transformerCapacity / (Math.sqrt(3) * sysUkv)) // kVA/kV → kA → 近似一次A的比例系数省略，工程近似
    : ratedCurrent;
  const effectiveRatedCurrent = derivedRated || ratedCurrent;

  // 1. 差动速断定值应躲过变压器励磁涌流，一般整定为5～8倍变压器额定电流
  const differentialInstantaneous = effectiveRatedCurrent * 6; // 取中间值6倍
  
  // 2. 比率制动特性斜率支持覆盖：默认0.5；若提示CT饱和则提高到0.6；可显式覆盖
  const rsInput = Number(params?.restraintSlope);
  const restraintSlope = isFinite(rsInput)
    ? Math.min(Math.max(rsInput, 0.2), 1.2)
    : (params?.ctSaturationHint ? 0.6 : 0.5);
  
  // 3. 二次谐波闭锁系数支持覆盖：默认0.18，范围0.05~0.35
  const shbInput = Number(params?.secondHarmonicBlocking);
  const secondHarmonicBlocking = isFinite(shbInput)
    ? Math.min(Math.max(shbInput, 0.05), 0.35)
    : 0.18;
  
  // 差动电流定值可配置（一般取0.3~0.5A，默认0.4A）
  const differentialCurrentSetting = (typeof params.diffPickupSecondary === 'number' && params.diffPickupSecondary > 0)
    ? params.diffPickupSecondary
    : 0.4;
  
  // 计算差动速断二次值
  const diffInstantaneousSecondary = differentialInstantaneous / ctRatio;

  // 默认分段构造：如用户未显式提供 segments，则生成两段：
  // 1) K=0 段至差动定值（水平线），2) K=restraintSlope 段延伸至无限
  const userSegments = Array.isArray(params?.segments)
    ? params.segments.map(seg => ({ slope: Number(seg.slope), threshold: Number(seg.threshold), label: seg.label, note: seg.note }))
    : [];
  const hasValidUserSegs = userSegments.some(sg => typeof sg.slope === 'number');
  const segments = hasValidUserSegs
    ? userSegments
    : [
        { slope: 0, threshold: parseFloat(differentialCurrentSetting.toFixed(2)), label: 'K=0' },
        { slope: parseFloat(restraintSlope.toFixed(2)), threshold: Infinity, label: `K=${parseFloat(restraintSlope.toFixed(2))}` }
      ];

  return {
    axesType: 'Id-Ir',
    differentialInstantaneous: parseFloat(differentialInstantaneous.toFixed(0)), // 一次值
    diffInstantaneousSecondary: parseFloat(diffInstantaneousSecondary.toFixed(3)), // 二次值
    differentialCurrentSetting: parseFloat(differentialCurrentSetting.toFixed(2)), // 差动电流定值
    restraintSlope: parseFloat(restraintSlope.toFixed(2)), // 比率制动斜率
    secondHarmonicBlocking: parseFloat(secondHarmonicBlocking.toFixed(2)), // 二次谐波制动系数
    ctRatio: ctRatio, // CT变比
    transformerCapacity: transformerCapacity, // 变压器容量
    ratedCurrent: effectiveRatedCurrent,
    characteristic: {
      slope: parseFloat(restraintSlope.toFixed(2)),
      diffPickup: parseFloat(differentialCurrentSetting.toFixed(2)),
      instantaneousSecondary: parseFloat(diffInstantaneousSecondary.toFixed(3)),
      segments
    }
  };
};

/**
 * 方向保护计算（功率方向元件简化）
 */
export const calculateDirectional = (params) => {
  const { systemVoltage, faultCurrent, ctRatio, ptRatio, polarizingAngle = 30, ratedCurrent = 0, curveType: curveInput, timeMultiplier } = params;
  const Vsec = (systemVoltage * 1000) / (ptRatio || 1);
  const Isec = (faultCurrent || 0) / (ctRatio || 1);
  const phi = (typeof params.phiVI_deg === 'number' && isFinite(params.phiVI_deg)) ? params.phiVI_deg : undefined; // 电压-电流相角差
  const wattComponent = phi !== undefined
    ? (Vsec * Isec * Math.cos((phi - polarizingAngle) * Math.PI / 180))
    : (Vsec * Isec * Math.cos(polarizingAngle * Math.PI / 180));
  const direction = wattComponent >= 0 ? '正向' : '反向';
  const pickupCurrent = (ratedCurrent * 1.1) / (ctRatio || 1);
  const sensitivity = pickupCurrent ? parseFloat((Isec / pickupCurrent).toFixed(2)) : null;
  const curveType = curveInput || 'Normal Inverse';
  const TMS = typeof timeMultiplier === 'number' && timeMultiplier > 0 ? timeMultiplier : 0.5;
  const M = pickupCurrent ? Isec / pickupCurrent : 0;
  const operateTime = computeInverseTime(M, TMS, curveType);
  const curvePoints = generateCurvePoints(TMS, curveType);
  const voltageThreshold = (typeof params.voltageThreshold === 'number' && params.voltageThreshold >= 0) ? params.voltageThreshold : 5; // 二次电压门限（V）
  return {
    axesType: 'I-t',
    polarizingAngle: parseFloat(polarizingAngle.toFixed(1)),
    secondaryVoltage: parseFloat(Vsec.toFixed(2)),
    secondaryCurrent: parseFloat(Isec.toFixed(2)),
    pickupCurrent: parseFloat(pickupCurrent.toFixed(3)),
    sensitivity,
    direction,
    operatingCharacteristic: '功率方向元件',
    timeMultiplier: TMS,
    curveType,
    operateTime,
    curvePoints,
    ...(phi !== undefined ? { phiVI_deg: parseFloat(phi.toFixed(1)) } : {}),
    voltageLow: Vsec < voltageThreshold
  };
};

/**
 * 零序保护计算（简化）
 */
export const calculateZeroSequence = (params) => {
  const { ratedCurrent, ctRatio, faultCurrent, curveType: curveInput, timeMultiplier } = params;
  // k0 推导：若显式提供则优先使用，否则根据接地方式给出近似值
  let k0 = (typeof params.k0 === 'number' && isFinite(params.k0)) ? params.k0 : undefined;
  if (k0 === undefined && typeof params.groundingMode === 'string') {
    const gm = params.groundingMode;
    if (gm === 'ungrounded') k0 = 0.15;
    else if (gm === 'resistance') k0 = 0.5;
    else if (gm === 'arc_suppression_coil') k0 = 0.3;
  }
  if (k0 === undefined) k0 = 0.3;
  const I0sec = ((faultCurrent || 0) * k0) / (ctRatio || 1);
  const pickupCurrent = (ratedCurrent * 0.4) / (ctRatio || 1);
  const sensitivity = pickupCurrent ? parseFloat((I0sec / pickupCurrent).toFixed(2)) : null;
  const curveType = curveInput || 'Normal Inverse';
  const TMS = typeof timeMultiplier === 'number' && timeMultiplier > 0 ? timeMultiplier : 0.5;
  const M = pickupCurrent ? I0sec / pickupCurrent : 0;
  const operateTime = computeInverseTime(M, TMS, curveType);
  const curvePoints = generateCurvePoints(TMS, curveType);
  return {
    axesType: 'I-t',
    zeroSequenceCurrent: parseFloat(I0sec.toFixed(3)),
    pickupCurrent: parseFloat(pickupCurrent.toFixed(3)),
    sensitivity,
    operatingCharacteristic: '零序电流保护',
    timeMultiplier: TMS,
    curveType,
    operateTime,
    curvePoints,
    k0: parseFloat(k0.toFixed(2))
  };
};

/**
 * 纵联保护计算（线路差动简化）
 */
export const calculateLongitudinal = (params) => {
  const { ratedCurrent, ctRatio, faultCurrent } = params;
  const I1sec = (faultCurrent || 0) / (ctRatio || 1);
  const I2sec = (typeof params.remoteFaultCurrent === 'number')
    ? (params.remoteFaultCurrent / (ctRatio || 1))
    : undefined;
  const pickupCurrent = (ratedCurrent * 1.2) / (ctRatio || 1);
  // 若提供对端电流，则采用 |I1 - I2| 作为差动，|I1 + I2| 作为制动；否则沿用简化
  const diffCurrent = (typeof I2sec === 'number')
    ? Math.abs(I1sec - I2sec)
    : I1sec;
  const restraintCurrent = (typeof I2sec === 'number')
    ? Math.abs(I1sec + I2sec)
    : undefined;
  const operateTime = 0.05; // 快速动作
  return {
    axesType: 'I-t',
    differentialCurrent: parseFloat(diffCurrent.toFixed(3)),
    pickupCurrent: parseFloat(pickupCurrent.toFixed(3)),
    operateTime: parseFloat(operateTime.toFixed(3)),
    operatingCharacteristic: '线路纵联差动',
    ...(typeof restraintCurrent === 'number' ? { restraintCurrent: parseFloat(restraintCurrent.toFixed(3)) } : {})
  };
};

/**
 * 保护定值计算主函数
 * @param {Object} values - 计算参数
 * @returns {Object} 计算结果
 */
export const calculateProtectionSettings = (values) => {
  const { protectionType } = values;
  
  switch(protectionType) {
    case 'overcurrent':
      return calculateOvercurrent(values);
    case 'distance':
      return calculateDistance(values);
    case 'differential':
      return calculateDifferential(values);
    case 'directional':
      return calculateDirectional(values);
    case 'zero_sequence':
      return calculateZeroSequence(values);
    case 'longitudinal':
      return calculateLongitudinal(values);
    default:
      return {};
  }
};

/**
 * 获取结果说明
 * @param {string} protectionType - 保护类型
 * @returns {Object} 结果说明
 */
export const getResultExplanation = (protectionType) => {
  switch(protectionType) {
    case 'overcurrent':
      return {
        title: '过电流保护定值说明',
        content: '过电流保护定值应可靠躲过最大负荷电流，并在保护范围末端故障时具有足够的灵敏度。启动电流通常设定为1.2~1.5倍最大负荷电流，时间设定需与相邻保护配合。IEC 曲线族选择建议：NI（Normal Inverse）适用于一般配电线路的广泛协调；VI（Very Inverse）适用于需与熔断器配合或远端故障电流较低场景；EI（Extremely Inverse）适用于熔断器协调与更强的反时限需求；LTI（Long-time Inverse）适用于过载长延时保护。常用 TMS 初始值范围：NI 0.10~0.20，VI 0.05~0.12，EI 0.05~0.08，LTI 0.30~0.60；实际取值需依据上下游选择性校核与现场试验微调。',
        warning: '注意与相邻保护的时间配合，确保选择性；考虑负荷自启动电流的影响，避免误动作。'
      };
    case 'distance':
      return {
        title: '距离保护定值说明',
        content: '距离保护通过测量故障点到保护安装处的阻抗来判断故障距离。通常分为三段式保护：第I段保护线路80-85%，第II段保护线路全长并延伸至下一线路，第III段作为远后备保护。',
        warning: '应校验在最小运行方式下的保护区长度；考虑弧光电阻对保护范围的影响，合理设定阻抗定值。'
      };
    case 'differential':
      return {
        title: '差动保护定值说明',
        content: '差动保护基于电流差值原理，对内部故障高度敏感，对外部故障具有良好的稳定性。差动速断定值应躲过变压器励磁涌流，比率制动特性斜率按各厂家推荐值整定。',
        warning: '应考虑CT饱和对保护性能的影响；二次谐波闭锁应合理设置，避免内部故障时因谐波含量高而拒动。'
      };
    case 'directional':
      return {
        title: '方向保护定值说明',
        content: '方向保护通过比较电压与电流的相位关系（功率方向元件/角度元件）判定故障方向，常用于配电网分段、线路双侧电源等场景。极化角与定值需结合系统潮流校核。若采用反时限动作，可参考 IEC 曲线族选型与 TMS 整定：NI 适用于一般协调、VI/EI 适用于与熔断器或远端低故障电流的协调、LTI 用于长延时场景。常用 TMS 初始值范围：NI 0.10~0.20，VI 0.05~0.12，EI 0.05~0.08，LTI 0.30~0.60；最终取值以选择性校核结果为准。',
        warning: '极化电压源选择（母线/线路）与接线方式需一致；在系统潮流反向或电压跌落场景下，注意方向元件的可靠性。'
      };
    case 'zero_sequence':
      return {
        title: '零序保护定值说明',
        content: '零序电流保护用于接地故障检测。定值需考虑系统接地方式与谐波电流影响，通常采用零序电流/零序功率元件组合。若采用反时限动作，IEC 曲线族可按场景选取：NI 用于常规配电线，VI/EI 便于与熔断器协调或远端低故障电流场景，LTI 用于长延时。常用 TMS 初始值范围：NI 0.10~0.20，VI 0.05~0.12，EI 0.05~0.08，LTI 0.30~0.60；并结合上下游保护配合与现场校核微调。',
        warning: '非故障线路可能存在感应零序电流，应合理设置低通滤波与延时，避免误动。'
      };
    case 'longitudinal':
      return {
        title: '纵联保护定值说明',
        content: '线路纵联差动保护通过两端电流量测比对实现快速选择性跳闸。通信通道的可靠性与匹配时延时是关键，定值需考虑CT误差与差动平衡。',
        warning: '通信异常或时钟不同步时需投入后备保护；整定应兼顾内部故障快速动作与外部故障稳定。'
      };
    default:
      return {
        title: '保护定值说明',
        content: '请选择保护类型进行计算。',
        warning: ''
      };
  }
};

/**
 * 验证输入参数
 * @param {Object} values - 输入参数
 * @returns {Object} 验证结果 {valid: boolean, errors: Array}
 */
export const validateInputs = (values) => {
  const errors = [];
  let valid = true;
  
  // 通用验证
  if (!values.protectionType) {
    errors.push('请选择保护类型');
    valid = false;
  }
  
  // 根据保护类型进行特定验证
  switch(values.protectionType) {
    case 'overcurrent':
      if (!values.ratedCurrent || values.ratedCurrent <= 0) {
        errors.push('额定电流必须大于0');
        valid = false;
      }
      if (!values.ctRatio || values.ctRatio <= 0) {
        errors.push('CT变比必须大于0');
        valid = false;
      }
      if (!values.faultCurrent || values.faultCurrent <= 0) {
        errors.push('故障电流必须大于0');
        valid = false;
      }
      if (values.timeMultiplier === undefined || values.timeMultiplier < 0.05 || values.timeMultiplier > 1.0) {
        errors.push('时间倍数 (TMS) 应在 0.05~1.00 范围');
        valid = false;
      }
      break;
      
    case 'distance':
      if (!values.systemVoltage || values.systemVoltage <= 0) {
        errors.push('系统电压必须大于0');
        valid = false;
      }
      if (!values.faultCurrent || values.faultCurrent <= 0) {
        errors.push('故障电流必须大于0');
        valid = false;
      }
      if (!values.ctRatio || values.ctRatio <= 0) {
        errors.push('CT变比必须大于0');
        valid = false;
      }
      if (!values.ptRatio || values.ptRatio <= 0) {
        errors.push('PT变比必须大于0');
        valid = false;
      }
      break;
      
    case 'differential':
      if (!values.ratedCurrent || values.ratedCurrent <= 0) {
        errors.push('额定电流必须大于0');
        valid = false;
      }
      if (!values.ctRatio || values.ctRatio <= 0) {
        errors.push('CT变比必须大于0');
        valid = false;
      }
      if (!values.transformerCapacity || values.transformerCapacity <= 0) {
        errors.push('变压器容量必须大于0');
        valid = false;
      }
      break;

    case 'directional':
      if (!values.systemVoltage || values.systemVoltage <= 0) {
        errors.push('系统电压必须大于0');
        valid = false;
      }
      if (!values.faultCurrent || values.faultCurrent <= 0) {
        errors.push('故障电流必须大于0');
        valid = false;
      }
      if (!values.ctRatio || values.ctRatio <= 0) {
        errors.push('CT变比必须大于0');
        valid = false;
      }
      if (!values.ptRatio || values.ptRatio <= 0) {
        errors.push('PT变比必须大于0');
        valid = false;
      }
      if (values.timeMultiplier === undefined || values.timeMultiplier < 0.05 || values.timeMultiplier > 1.0) {
        errors.push('时间倍数 (TMS) 应在 0.05~1.00 范围');
        valid = false;
      }
      if (values.polarizingAngle !== undefined && (values.polarizingAngle < 0 || values.polarizingAngle > 90)) {
        errors.push('极化角 (°) 应在 0~90 范围');
        valid = false;
      }
      break;

    case 'zero_sequence':
      if (!values.ratedCurrent || values.ratedCurrent <= 0) {
        errors.push('额定电流必须大于0');
        valid = false;
      }
      if (!values.ctRatio || values.ctRatio <= 0) {
        errors.push('CT变比必须大于0');
        valid = false;
      }
      if (!values.faultCurrent || values.faultCurrent <= 0) {
        errors.push('故障电流必须大于0');
        valid = false;
      }
      if (values.timeMultiplier === undefined || values.timeMultiplier < 0.05 || values.timeMultiplier > 1.0) {
        errors.push('时间倍数 (TMS) 应在 0.05~1.00 范围');
        valid = false;
      }
      if (values.k0 !== undefined && (values.k0 < 0 || values.k0 > 1)) {
        errors.push('零序比例系数 k0 应在 0~1 范围');
        valid = false;
      }
      break;

    case 'longitudinal':
      if (!values.ratedCurrent || values.ratedCurrent <= 0) {
        errors.push('额定电流必须大于0');
        valid = false;
      }
      if (!values.ctRatio || values.ctRatio <= 0) {
        errors.push('CT变比必须大于0');
        valid = false;
      }
      if (!values.faultCurrent || values.faultCurrent <= 0) {
        errors.push('故障电流必须大于0');
        valid = false;
      }
      break;
  }
  
  return { valid, errors };
};